import React, { useEffect } from 'react';
import './KodanshaSeries.scss';
import { SeriesPictureAndBreadCrumb, SeriesTitleAndDescription } from './Header/Header';
import SeriesInfo from './ComicInfo/SeriesInfo';
import DesktopRating from './Desktop/DesktopRating/DesktopRating';
import { KodanshaNavbar } from '../../KodanshaNavbar/KodanshaNavbar';
import BrowseManga from './BrowseManga/BrowseManga';
import { useHistory, useParams } from 'react-router-dom';
import { useAtom, useAtomValue } from 'jotai';
import { KodanshaFooter } from '../../KodanshaFooter/KodanshaFooter';
import KodanshaSubscription from '../../KodanshaSubscription/KodanshaSubscription';
import KodanshaAvailable from '../../KodanshaUIKit/KodanshaAvailable/KodanshaAvailable';
import ReactGA from 'react-ga4';
import { Helmet } from 'react-helmet';
import { KodanshaNotFound } from '../../KodanshaNotFound/KodanshaNotFound';
import Log from '../../Services/Logger';
import {
    FetchSingleSeriesAtom,
    LoginStatusForSeriesAtom,
    SingleSeriesAtom,
    SingleSeriesErrorAtom,
} from '../../../store/SeriesStore';
import { RenderChapterToRead } from './RenderChapterToRead';
import { LoginStatus, LoginStatusAtom } from '../../../store/AccountStore';
import { getMetaDescription } from '../../../utils/seo';

interface KodanshaSeriesParams {
    sid: string;
}

export function KodanshaSeries(): JSX.Element {
    const { sid } = useParams<KodanshaSeriesParams>();
    const error = useAtomValue(SingleSeriesErrorAtom);
    const [finalSeries, fetchSeries ] = useAtom(FetchSingleSeriesAtom);
    const [lastLoginStatus, setLastLoginStatus] = useAtom(LoginStatusForSeriesAtom);
    const loginStatus = useAtomValue(LoginStatusAtom);
    const history = useHistory();

    useEffect(() => {
        Log.debug(`Series trigger: sid ${sid}, loginStatus: ${loginStatus}`);
        if(loginStatus === "LoggedIn" || loginStatus === "LoggedOut") {
            //Ensure that we only load if the login status has actually changed.
            if(finalSeries?.id.toString() !== sid && finalSeries?.readableUrl !== sid || loginStatus !== lastLoginStatus) {
                Log.info("Loading series...");
                setTimeout(() => {
                    window.scrollTo(0, 0);
                });
                fetchSeries(sid);
            }
        }
    }, [sid, loginStatus]);

    useEffect(() => {
        if (finalSeries !== null)
            ReactGA.send({ hitType: 'pageview', page: window.location.href, title: 'Series | ' + finalSeries.title });
    }, [finalSeries]);

    useEffect(() => {
        if (finalSeries?.readableUrl && finalSeries.id.toString() === sid){
            history.replace("/series/"+ finalSeries.readableUrl);
        }
    }, [finalSeries, sid]);


    Log.info("Render Series...");

    function SeriesHelmet() {
        const series = useAtomValue(SingleSeriesAtom);

        Log.info("Render Series Helmet...");
        return (
            <Helmet>
                {/*Basic SEO Tags*/}
                <title>{series?.title ?? 'Kodansha – Inspire Impossible Stories'}</title>
                <meta name='description' content={getMetaDescription(series?.metaDescription, series?.description)} />
                {series?.readableUrl && (
                    <link rel='canonical'
                          href={'/series/' + series.readableUrl + '/'} />
                )}

                {/*OpenGraph*/}
                <meta property='og:title' content={series?.title} />
                <meta property='og:type' content='book' />
                <meta property='og:description' content={series?.description} />
                <meta property='og:site_name' content='Kodansha' />
                <meta property='og:locale' content='en_GB' />
                <meta property='og:image' content={series?.thumbnails[0]?.url} />

                {/*Twitter*/}
                <meta property='twitter:card' content='summary' />
                <meta property='twitter:site' content='@KodanshaManga' />
                <meta property='twitter:title' content={series?.title} />
                <meta property='twitter:description' content={series?.description} />
                <meta property='twitter:image:src' content={series?.thumbnails[0]?.url} />
                <meta property='twitter:image:alt' content={'Image from ' + series?.title} />
                <meta property='twitter:domain' content='Kodansha' />
            </Helmet>
        );
    }

    if (error === 'NotFound') {
        return (<KodanshaNotFound />);
    }

    if (error === 'GeoRestricted') {
        return (<KodanshaNotFound />);
    }

    if(finalSeries?.id.toString() !== sid && finalSeries?.readableUrl !== sid) {
        return null;
    }

    return (
        <>
            <SeriesHelmet/>
            <main className='series-container'>
                <div className='series-max-wrapper'>
                    <KodanshaNavbar />
                    <div className='header-on-mobile'>
                        <div className='series-grid-header'>
                            <div className='series-desktop-header-info-wrapper-series'>
                                <SeriesTitleAndDescription />
                                <div className='series-desktop-reader-chapter'>
                                    <RenderChapterToRead />
                                </div>
                            </div>
                            <SeriesPictureAndBreadCrumb />
                        </div>
                    </div>

                    {/*<TagTable series={series} />*/}
                    <DesktopRating />

                    <div className='read-chapters-in-series-on-mobile'>
                        <RenderChapterToRead />
                    </div>
                </div>
                {/*<SimulPub />*/}
                {/* TODO: Post Beta Feature */}
                {/*hidee*/}
                {/*<Awards />*/}
                {/*hidee*/}
                <div className='series-max-wrapper'>
                    <SeriesInfo />
                    {/*hidee*/}

                    <div className='kodansha-available-on-mobile'>
                        <KodanshaAvailable items={[]} />
                    </div>

                    <BrowseManga />
                </div>
            </main>
            <KodanshaSubscription />
            <KodanshaFooter />
        </>
    );
}

export default KodanshaSeries;